import {
  ErrorMessage, Form, Formik, FormikProps, FormikTouched, FormikValues,
} from 'formik'
import moment from 'moment'
import React, {
  useEffect, useMemo, useState,
  useCallback,
  useRef,
} from 'react'
import Modal from 'src/components/Modal'
import NumberFormat from 'react-number-format'
import { toast } from 'react-toastify'
import { getBatchFraction } from 'src/services/receivement/receivementService'
import * as Yup from 'yup'
import Button from '../../../../components/Button'
import FlexLayout from '../../../../components/FlexLayout'
import FormField from '../../../../components/Form/FormField'
import Text from '../../../../components/Text'
import Helper from '../../../../services/Helper'
import stockapi from '../../../../services/StockApi'
import { ModalAlertTotalFractioned } from '../ModalAlertTotalFractioned'
import useDeleteFract from '../ModalDeleteFract/useDeleteFract'
import useTotalLoss from '../ModalTotalLoss/useTotalLoss'
import {
  FinishFractionFormWrapper, FieldAdornment, CustomField,
} from './style'
import {
  createFractionListItem,
} from '../../FractionUtils'
import {
  Batch, BatchFraction, FinishFraction, FractionItem, PrintFractionTagPayload, TouchedValues,
} from '../../FractionTypes'
import theme from 'src/styles/theme'
import ImageContainer from '../ImageContainer'
import FractionTable from '../FractionTable'
import FractionInput from '../FractionInput'
import { formatValueFromIsKg } from 'src/utils'
import BarcodeProductInput from '../BarcodeProductInput'
import FinishButton from '../FinishButton'
import { findDescription } from 'src/services/product/productService'
import { actions as fractionActions } from '../../FractionState'
import { useDispatch } from 'react-redux'
import ModalAlertFractionValue from '../ModalAlertFractionValue/ModalAlertFractionValue'
import { useSelector } from 'src/utils/store'
import dayjs from 'dayjs'

interface FractionLotProps {
  receivementId?: number
  modelId?: number
  batch: Batch
  resetFraction: () => void
  updateTableLot: () => Promise<void>
  setLoad: (value: boolean) => void
}

function FractionLot({
  receivementId, modelId, batch, resetFraction, updateTableLot, setLoad,
}: FractionLotProps) {
  const dispatch = useDispatch()

  const { isRemotePrinterConnected, fractionTag } = useSelector((state) => state.fraction)

  const formikRef = useRef<FormikProps<FormikValues>>(null)

  const [fractionTable, setFractionTable] = useState<FractionItem[]>([])
  const [averageWeight, setAverageWeight] = useState<number>()
  const [handleClickFractRemove, RemoveFractionModalComponent] = useDeleteFract({ setFractionTable })
  const { handleClickTotalLoss, totalLossModalComponent } = useTotalLoss({ updateTableLot, resetFraction })
  const [badgeUser, setBadgeUser] = useState<string>()

  const [error, setError] = useState('')

  const [prevFraction, setPrevFraction] = useState<number>()
  const [showAlertTotalFractioned, setShowAlertTotalFractioned] = useState(false)

  const [showAlertFractionValue, setShowAlertFractionValue] = useState(false)
  const [confirmedFractionValue, setConfirmedFractionValue] = useState(false)
  const [confirmedProduct, setConfirmedProduct] = useState(false)

  const [amountRejected, setAmountRejected] = useState(0)
  const [finishFractionObject, setFinishFractionObject] = useState<FinishFraction>({})

  const [showModal, setShowModal] = useState(false)
  const handleCloseModal = () => setShowModal(false)

  const [totalFractioned, setTotalFractioned] = useState<number>(0)

  const preventDoubleClick = useRef(false)

  const formattedPrintTag = (_batch: Batch, _batchFraction: BatchFraction): PrintFractionTagPayload => (
    {
      productDescription: _batch.descricao,
      productCode: `Código: ${_batch.cod_produto}`,
      productBarcode: _batch.cod_produto,
      batchId: `Id_lote: ${_batch.id_lote}`,
      fractionInclusionDate: `Fracionado: ${dayjs(_batchFraction.data_inclusao).format('DD/MM/YYYY')}`,
      weight: `Peso: ${_batchFraction.quantidade} Kg`,
    }
  )

  const fetchBatchFraction = useCallback(async () => {
    const res = await getBatchFraction(batch.id_lote)
    if (!res.isOk) {
      const message = 'Não foi possível consultar os fracionamentos desse lote'
      toast.error(message)
      setError(message)
      return
    }

    setAverageWeight(res.data.product_average_weight)

    const fractionsList = res.data.fractions.reverse().map((item, index) => {
      if (isRemotePrinterConnected && fractionTag && batch.fractioning_tag) {
        const handleClickFractPrint = () => {
          dispatch(fractionActions.printFractionTag(formattedPrintTag(batch, item)))
        }

        return createFractionListItem(item, handleClickFractRemove, index === 0, handleClickFractPrint)
      }
      return createFractionListItem(item, handleClickFractRemove, index === 0)
    })

    setFractionTable(fractionsList)
    setError('')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, handleClickFractRemove, dispatch])

  const calcTotalFractioned = useCallback(() => {
    const totalFractionedResult = Array.from(fractionTable).map((item) => item.qty * 1000).reduce((a, b) => a + b, 0) + batch.quantidade_rejeitada * 1000
    return totalFractionedResult
  }, [fractionTable, batch])

  const handleAddFractionTable = (values, setFieldValue, setSubmitting) => {
    const checkFractionValueConfirmed = (fraction: number) => {
      const parsedFraction = fraction / 1000
      if (parsedFraction !== prevFraction || !confirmedFractionValue) {
        if (averageWeight) {
          if (
            parsedFraction < averageWeight * 0.5
            || parsedFraction > averageWeight * 1.5
          ) {
            setPrevFraction(parsedFraction)
            if (confirmedProduct) setShowAlertFractionValue(true)
            setSubmitting(false)
            return false
          }
        }
      }
      return true
    }
    setSubmitting(true)

    const options = {
      headers: {
        'user-barcode': values.userId,
      },
    }

    const quantidadeFracao = values.fracao

    if (!checkFractionValueConfirmed(quantidadeFracao)) {
      return
    }
    setPrevFraction(undefined)
    setShowAlertFractionValue(false)
    setConfirmedFractionValue(false)

    const data = {
      quantidade: quantidadeFracao / 1000,
      id_lote: batch.id_lote,
      data_validade: finishFractionObject.expirationDate,
    }

    stockapi
      .post('/recebimento/fracionar', data, options)
      .then((res) => {
        let newFraction: FractionItem

        if (isRemotePrinterConnected && fractionTag && batch.fractioning_tag) {
          const printFractionTagPayload = formattedPrintTag(batch, res.data.fracao)

          const handleClickFractPrint = () => {
            dispatch(fractionActions.printFractionTag(printFractionTagPayload))
          }

          newFraction = createFractionListItem(res.data.fracao, handleClickFractRemove, true, handleClickFractPrint)
        } else {
          newFraction = createFractionListItem(res.data.fracao, handleClickFractRemove, true)
        }

        setFractionTable((prevTable) => {
          if (prevTable.length) prevTable[0].styleCell = {}
          return [newFraction, ...prevTable]
        })

        setFieldValue('fracao', '', false)
        setError('')

        formikRef.current?.setFieldValue('barcodeProduct', '')
        dispatch(fractionActions.clearField('scaleWeight'))
      })
      .catch((err) => {
        if (err.response && err.response?.data) {
          setError(err.response.data.message)
        } else {
          setError(
            'Não foi possivel adicionar o fracionamento, tente novamente',
          )
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const validateTotalFractioned = () => {
    const total = batch.is_kg ? batch.quantidade_total * 1000 : Math.trunc(batch.quantidade_total)

    const totalFractionedFromIsKg = batch.is_kg ? totalFractioned : fractionTable.length

    const totalFractionedAmount = totalFractionedFromIsKg + amountRejected

    if (batch.is_kg) {
      if (total <= 1000) return totalFractionedAmount >= total - 100 && totalFractionedAmount <= total + 100
      return totalFractionedAmount >= total * 0.9 && totalFractionedAmount <= total * 1.1
    }

    return totalFractionedAmount === total
  }

  const postFinishFraction = useCallback((setSubmitting) => {
    if (preventDoubleClick.current) {
      return
    }

    preventDoubleClick.current = true

    if (setSubmitting) setSubmitting(true)

    const options = {
      headers: {
        'user-barcode': finishFractionObject.userId,
      },
    }

    const data = {
      quantidade_rejeitada: batch.is_kg ? amountRejected / 1000 : amountRejected,
    }

    stockapi
      .post(`/recebimento/${batch.id_lote}/finalizar`, data, options)
      .then(() => {
        updateTableLot()
        resetFraction()
        setShowAlertTotalFractioned(false)
      })
      .catch((err) => {
        setShowAlertTotalFractioned(false)
        handleCloseModal()
        if (err.response) setError(err.response.data.message)
      })
      .finally(() => {
        if (setSubmitting) setSubmitting(false)
        preventDoubleClick.current = false
      })
  }, [finishFractionObject.userId, batch.is_kg, batch.id_lote, amountRejected, updateTableLot, resetFraction])

  const handleFinishFraction = (setSubmitting) => {
    if (!validateTotalFractioned()) {
      handleCloseModal()
      setShowAlertTotalFractioned(true)
      return
    }
    postFinishFraction(setSubmitting)
  }

  const handleChangeUserId = async (value: string) => {
    const isValid = await Helper.validateBarcodeUser(value)
    setBadgeUser(isValid ? value : undefined)

    setFinishFractionObject((prev) => ({
      ...prev,
      userId: value,
      expirationDate: batch.data_validade,
    }))

    return isValid
  }

  const alertTotalFractioned = useMemo(
    () =>
      showAlertTotalFractioned && (
        <ModalAlertTotalFractioned
          showModal={showAlertTotalFractioned}
          finishFraction={postFinishFraction}
          setShowModal={setShowAlertTotalFractioned}
          totalFractioned={totalFractioned + amountRejected}
          totalFractions={fractionTable.length + amountRejected}
          totalQuantity={batch.quantidade_total}
          measureUnit={`${batch.unidade_medida_abreviatura ? (batch.unidade_medida_abreviatura).toUpperCase() : ''}`}
          isKg={batch.is_kg}
        />
      ),
    [showAlertTotalFractioned, postFinishFraction, batch.is_kg, batch.quantidade_total, batch.unidade_medida_abreviatura, totalFractioned, fractionTable.length, amountRejected],
  )

  const finishFraction = async (setFieldTouched, validateForm) => {
    setFieldTouched('expirationDate', true)
    setFieldTouched('userId', true)
    await validateForm().then((errors) => {
      if (!errors.userId && !errors.expirationDate) {
        setShowModal(true)
      }
    })
  }

  const validateRebeepBarcodeProduct = React.useCallback(async (
    barcode?: string,
  ) => {
    try {
      if (!barcode) return new Yup.ValidationError('form.required', barcode, 'barcodeProduct')
      const { id_modelo } = await findDescription(
        barcode,
        receivementId,
      )

      if (id_modelo === 'NAO CADASTRADO') {
        return new Yup.ValidationError('form.rebeep.notFound', barcode, 'barcodeProduct')
      }

      if (id_modelo !== modelId) {
        return new Yup.ValidationError('form.rebeep.anotherProduct', barcode, 'barcodeProduct')
      }
      setConfirmedProduct(true)

      return true
    } catch (e) {
      setConfirmedProduct(false)

      toast.error('Problema ao buscar produto')
      formikRef.current?.setFieldValue('barcodeProduct', '')

      return new Yup.ValidationError('form.required', barcode, 'barcodeProduct')
    }
  }, [receivementId, modelId])

  useEffect(() => {
    if (!batch.id_lote) return

    (async () => {
      setLoad(true)
      await fetchBatchFraction()
      setLoad(false)
    })()
  }, [fetchBatchFraction, handleClickFractRemove, batch, setLoad])

  useEffect(() => {
    setTotalFractioned(calcTotalFractioned())
  }, [calcTotalFractioned, fractionTable])

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          fracao: undefined,
          userId: '',
          product: batch.descricao,
          provider: batch.nome_fornecedor,
          expirationDate: moment(batch.data_validade, 'YYYY-MM-DD').format(
            'DD/MM/YYYY',
          ),
          qntLot: batch.quantidade_total,
          barcodeProduct: undefined,
        }}
        validationSchema={Yup.object().shape({
          fracao: Yup.string()
            .required('form.required')
            .test(
              'fracao',
              'fraction.form.fractionQtdWarning',
              (values) => parseFloat(values as string) > 0,
            ),
          userId: Yup.string()
            .required('form.required')
            .test(
              'barcodeUser',
              'receivement.form.error.barcodeUser',
              (values) => handleChangeUserId(values || ''),
            ),
          expirationDate: Yup.date()
            .transform((value, originalValue) =>
              moment(originalValue, 'DD/MM/YYYY').toDate())
            .required('form.required'),
          barcodeProduct: Yup.string()
            .required('form.required')
            .test(
              'barcodeProduct',
              'barcodeProduct validation error',
              (value) => validateRebeepBarcodeProduct(value || undefined),
            ),
        })}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={(values, {
          setSubmitting, setFieldValue,
        }) => {
          const fracao = values?.fracao
          if (fracao && fracao > 0) {
            setFinishFractionObject((prev: any) => ({
              ...prev,
              userId: values.userId,
              expirationDate: moment(
                values.expirationDate,
                'DD/MM/YYYY',
              ).format('YYYY-MM-DD'),
            }))
            handleAddFractionTable(values, setFieldValue, setSubmitting)

            document.getElementById('fracao')?.focus()
          }
        }}
      >
        {({
          values,
          handleSubmit,
          isSubmitting,
          validateForm,
          setFieldTouched,
          setTouched,
          validateField,
          setFieldValue,
        }) => (
          <Form style={{ width: '100%' }}>
            <FlexLayout>
              <FlexLayout direction='column' style={{ width: '40%' }}>
                <FormField label='product' name='product' isDisabled />
                <FormField
                  label='fraction.form.totalQuantity'
                  name='qntLot'
                  isDisabled
                  adornment={<FieldAdornment>{batch.unidade_medida_abreviatura.toUpperCase()}</FieldAdornment>}
                />
                <FormField label='provider' name='provider' isDisabled />
                <FormField
                  testId='input-user-badge'
                  label='userId'
                  fieldType='password'
                  name='userId'
                  autoComplete='off'
                  onBlur={() => {
                    validateField('userId')
                    setTouched(values as unknown as FormikTouched<TouchedValues>)
                  }}
                />

                <FormField
                  label='receivement.form.expirationDate'
                  name='expirationDate'
                  isDisabled
                />
                {error && <Text style={{ color: theme.colors.red }} plainText={error}/>}

                <FlexLayout
                  justify={fractionTable.length === 0 ? 'between' : 'end'}
                  style={{ margin: '15px 0 0 0' }}
                >
                  {fractionTable.length === 0 && (
                  <Button
                    type='button'
                    text='fraction.form.totalLoss'
                    style={{ background: theme.colors.red }}
                    onClick={() => handleClickTotalLoss(batch)}
                    onKeyDown={(e) => e.keyCode === 32 && e.preventDefault()}
                  />
                  )}

                  <Button
                    type='button'
                    text='back'
                    onClick={() => {
                      updateTableLot()
                      resetFraction()
                    }}
                    onKeyDown={(e) => e.keyCode === 32 && e.preventDefault()}
                  />
                </FlexLayout>

                <FractionInput
                  averageWeight={averageWeight}
                  values={values as TouchedValues}
                  setFieldValue={setFieldValue}
                  validateField={validateField}
                  errorMessage={(
                    <ErrorMessage
                      name='fracao'
                      render={(msg) => <Text style={{ color: theme.colors.red2 }}>{msg}</Text>}
                    />
                  )}
                  batch={batch}
                />

                <BarcodeProductInput
                  value={values.barcodeProduct}
                  setFieldValue={setFieldValue}
                  validateField={validateField}
                  errorMessage={(
                    <ErrorMessage
                      name='barcodeProduct'
                      render={(msg) => <Text testId='barcodeProductError' style={{ color: theme.colors.red2 }}>{msg}</Text>}
                    />
                  )}
                />

                <FinishButton
                  isSubmitting={isSubmitting}
                  handleSubmit={handleSubmit}
                />
                <FractionTable table={fractionTable} batch={batch} finishAction={() => finishFraction(setFieldTouched, validateForm)} totalFractioned={totalFractioned} />
                <ModalAlertFractionValue
                  confirmAction={() => handleSubmit()}
                  setIsOpen={setShowAlertFractionValue}
                  setConfirmedFractionValue={setConfirmedFractionValue}
                  isOpen={showAlertFractionValue}
                />
              </FlexLayout>
              <FlexLayout
                direction='column'
                style={{ width: '70%' }}
                justify='center'
              >
                <FlexLayout
                  justify='center'
                  style={{ margin: '0 0 0 30px', width: '90%' }}
                >
                  <ImageContainer imageUrl={batch.arquivo} />
                </FlexLayout>
              </FlexLayout>
            </FlexLayout>
          </Form>

        )}
      </Formik>

      <Modal
        title='Finalizar Lote'
        closedButton
        closeButtonAction={handleCloseModal}
        show={showModal}
        body={(
          <FinishFractionFormWrapper>
            <Formik
              initialValues={{
                amountRejected: 0,
              }}
              validationSchema={Yup.object().shape({
                amountRejected: Yup.string().required('form.required'),
              })}
              validateOnBlur
              validateOnChange={false}
              onSubmit={(values, { setSubmitting }) => {
                handleFinishFraction(setSubmitting)
              }}
            >
              {({ isSubmitting, handleSubmit }) => (
                <Form style={{ width: '100%' }}>
                  <FlexLayout align='center' direction='column'>
                    <FlexLayout direction='column'>
                      <Text
                        textType='defaut'
                        text={batch.is_kg ? 'fraction.form.amountRejected.kg' : 'fraction.form.amountRejected.un'}
                      />
                      <NumberFormat
                        name='amountRejected'
                        decimalSeparator=','
                        thousandSeparator='.'
                        decimalScale={3}
                        allowLeadingZeros={false}
                        allowNegative={false}
                        fixedDecimalScale
                        allowEmptyFormatting={false}
                        defaultValue=''
                        value={amountRejected}
                        format={(value) => formatValueFromIsKg(value, batch.is_kg)}
                        onValueChange={(val) =>
                          setAmountRejected((parseFloat(val.value) * 1000) / 1000)}
                        style={CustomField}
                        maxLength={10}
                        data-testid='input-amountRejected'
                      />
                      <ErrorMessage component={Text} name='amountRejected' />
                    </FlexLayout>
                    <Button
                      isDisabled={isSubmitting}
                      type='submit'
                      text='fraction.form.finishLot'
                      testId='button-amount-rejected-finish'
                      style={{ width: '200px', marginTop: '20px' }}
                      onClick={handleSubmit}
                    />
                  </FlexLayout>
                </Form>
              )}
            </Formik>
          </FinishFractionFormWrapper>
        )}
      />

      {RemoveFractionModalComponent(batch)}
      {totalLossModalComponent(badgeUser || '')}
      {alertTotalFractioned}
    </>
  )
}

export default FractionLot
